import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  Space,
  DatePicker,
  InputNumber
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showError, showSuccess } from '../../../../components/errorHandler';
import dayjs from 'dayjs';

const { Option } = Select;

interface LeadModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: () => void; // To refetch after insert
  initialValues?: {
    lead_id?: number;
    lead_name?: string;
    lead_class?: string;
    lead_father_name?: string;
    lead_guardian_name?: string;
    lead_mother_name?: string;
    lead_dob?: string;
    lead_address?: string;
    lead_mobile?: number;
    lead_pin_code?: string;
    lead_aadhar_number?: string;
    lead_gender?: string;
    lead_blood_group?: string;
    lead_remark?: string;
    lead_image?: File | string;
  } | null;
  isEditMode?: boolean;
}

const LeadModal: React.FC<LeadModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const [form] = Form.useForm();
  const [classOptions, setClassOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (initialValues) {
        // Convert dob string to dayjs object for DatePicker
        const dob = initialValues.lead_dob ? dayjs(initialValues.lead_dob) : null;
        form.setFieldsValue({ ...initialValues, dob });
      }
      fetchInitialData();
    }
  }, [visible, initialValues]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [classes, users] = await Promise.all([
        apiRequest({ method: 'get', url: '/classes' }),
        apiRequest({ method: 'get', url: '/users' }),
      ]);
      setClassOptions(classes);
      setUserOptions(users);
    } catch (err) {
      showError('Failed to fetch class or user data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        lead_name: values.lead_name,
        lead_class: values.lead_class,
        lead_father_name: values.lead_father_name,
        lead_guardian_name: values.lead_guardian_name,
        lead_mother_name: values.lead_mother_name,
        lead_dob: values.dob ? values.dob.format('YYYY-MM-DD') : null,
        lead_address: values.lead_address,
        lead_mobile: values.lead_mobile,
        lead_pin_code: values.lead_pin_code,
        lead_aadhar_number: values.lead_aadhar_number,
        lead_gender: values.lead_gender,
        lead_blood_group: values.lead_blood_group,
        lead_remark: values.lead_remark,
        ...(initialValues?.lead_id && { lead_id: initialValues.lead_id.toString() }),
      };
      
      const url = isEditMode ? 'edit-lead' : 'add-lead';
      const method = isEditMode ? 'put' : 'post';
      await apiRequest({ method: method, url, data: payload });

      showSuccess('Lead added successfully!');
      onCreate();
      onCancel();
    } catch (error) {
      showError('Failed to add lead');
    }
  };
  useEffect(() => {
    if (visible) {
      if (isEditMode && initialValues) {
        form.setFieldsValue({
          lead_name: initialValues.lead_name,
          lead_class: initialValues.lead_class,
          lead_father_name: initialValues.lead_father_name,
          lead_guardian_name: initialValues.lead_guardian_name,
          lead_mother_name: initialValues.lead_mother_name,
          lead_dob: initialValues.lead_dob ? initialValues.lead_dob : null,
          lead_address: initialValues.lead_address,
          lead_mobile: initialValues.lead_mobile,
          lead_pin_code: initialValues.lead_pin_code,
          lead_aadhar_number: initialValues.lead_aadhar_number,
          lead_gender: initialValues.lead_gender,
          lead_blood_group: initialValues.lead_blood_group,
          lead_remark: initialValues.lead_remark,
        });

        // Set preview for student image if exists

      } else {
        form.resetFields();
      }
    }
  }, [visible, isEditMode, initialValues, form]);

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title="Add Lead"
      destroyOnClose
      centered
      width="90vw" // almost full width
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 24 }} // scroll only body
      footer={(
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()}>
            Submit
          </Button>
        </Space>
      )}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="lead_name"
              label="Lead Name"
              rules={[{ required: true, message: 'Please enter lead name' }]}
            >
              <Input placeholder="Enter lead name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="lead_mobile" label="Mobile Number">
              <InputNumber
                className="py-7 border-radious"
                placeholder="Enter Mobile Number"
                maxLength={10}
                minLength={10}
                style={{ width: '100%' }}
                controls={false}
                onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const nativeEvent = e.nativeEvent as InputEvent;
                  const inputChar = nativeEvent.data;
                  const currentValue = (e.target as HTMLInputElement).value;

                  if (!inputChar || !/^\d$/.test(inputChar) || currentValue.length >= 10) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lead_class"
              label="Class"
              rules={[{ required: true, message: 'Please select class' }]}
            >
              <Select placeholder="Select class" loading={loading}>
                {classOptions.map((cls) => (
                  <Option key={cls.class_id} value={cls.class_id}>
                    {cls.class_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="lead_blood_group" label="Blood Group">
              <Select placeholder="Select blood group" allowClear>
                {[
                  'A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-',
                ].map((bg) => (
                  <Option key={bg} value={bg}>
                    {bg}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>


        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="lead_father_name" label="Father's Name">
              <Input placeholder="Enter father's name" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="lead_guardian_name" label="Guardian's Name">
              <Input placeholder="Enter guardian's name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="lead_mother_name" label="Mother's Name">
              <Input placeholder="Enter mother's name" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="dob" label="Date of Birth">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="lead_remark" label="Remarks">
              <Input.TextArea placeholder="Enter remark" rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="lead_address" label="Address">
              <Input.TextArea placeholder="Enter address" rows={3} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="lead_pin_code" label="Pin Code">
              <InputNumber
                className="py-7 border-radious"
                placeholder="Enter pin code"
                maxLength={6}
                minLength={0}
                min={100000}
                max={999999}
                style={{ width: '100%' }}
                controls={false}
                onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const nativeEvent = e.nativeEvent as InputEvent;
                  const inputChar = nativeEvent.data;
                  const currentValue = (e.target as HTMLInputElement).value;

                  if (!inputChar || !/^\d$/.test(inputChar) || currentValue.length >= 6) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="lead_aadhar_number" label="Aadhar Number">
              <InputNumber
                className="py-7 border-radious"
                placeholder="Enter Aadhaar"
                maxLength={12}
                minLength={0}
                style={{ width: '100%' }}
                controls={false}
                onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const nativeEvent = e.nativeEvent as InputEvent;
                  const inputChar = nativeEvent.data;
                  const currentValue = (e.target as HTMLInputElement).value;

                  if (!inputChar || !/^\d$/.test(inputChar) || currentValue.length >= 12) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="lead_gender" label="Gender">
              <Select placeholder="Select gender" allowClear>
                <Option value="Male">Male</Option>
                <Option value="Female">Female</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default LeadModal;
